<template>
  <b-modal v-model="showModal" title="Enter Auto Mode?" @ok="onConfirm" @cancel="onCancel" ok-title="Enter Auto Mode"
    cancel-title="Cancel" ok-variant="primary" centered>
    <p class="mb-3">
      <strong>Auto Mode</strong> will automatically navigate you to your next pending task based on queue priority.
    </p>

    <p class="mb-3">
      When you complete a task, the system will automatically fetch and navigate to the next item in your queue.
    </p>

    <label>
      Working Queue
      <HelpPopup>
        Select a specific queue to only work items from that queue, or choose "All Queues"
      </HelpPopup>
    </label>
    <b-form-group label-for="specific-queue" class="mb-0">
      <b-form-select id="specific-queue" v-model="selectedQueue" :options="queueOptions" :disabled="loadingQueues">
        <template #first>
          <b-form-select-option v-if="loadingQueues" :value="null" disabled>
            Loading queues...
          </b-form-select-option>
          <b-form-select-option v-else :value="null">
            All Queues
          </b-form-select-option>
        </template>
      </b-form-select>
    </b-form-group>
  </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import AutoModeService from '@/services/AutoModeService';
import HelpPopup from '../HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({ components: { HelpPopup } })
export default class AutoModeConfirmModal extends Vue {
  @Prop({ type: Boolean, default: false }) readonly show!: boolean;

  private showModal: boolean = false;
  private selectedQueue: string | null = null;
  private availableQueues: string[] = [];
  private loadingQueues: boolean = false;

  get queueOptions(): Array<{ label?: string; options?: Array<{ value: string; text: string }>; value?: string; text?: string }> {
    // If no queues are available, return empty array
    if (this.availableQueues.length === 0) {
      return [];
    }

    // Create optgroup with all individual queues
    return [
      {
        label: 'Individual Queues',
        options: this.availableQueues.map(queue => ({
          value: queue,
          text: queue
        }))
      }
    ];
  }

  @Watch('show', { immediate: true })
  async onShowChanged(newVal: boolean): Promise<void> {
    this.showModal = newVal;

    if (newVal) {
      await this.loadAvailableQueues();
      this.loadSavedPreference();
    }
  }

  @Watch('showModal')
  onShowModalChanged(newVal: boolean): void {
    if (!newVal && newVal !== this.show) {
      this.$emit('update:show', false);
    }
  }

  private async loadAvailableQueues(): Promise<void> {
    this.loadingQueues = true;
    try {
      this.availableQueues = await AutoModeService.getAvailableQueues();
    } catch (error) {
      console.error('Failed to load available queues:', error);
      this.$notification(NotificationOptions.error("Failed to load available queues"));
      this.availableQueues = ['Ingestion', 'Allergy', 'Online Order', 'Insurance', 'PV1', 'Consultation', 'Transfer', 'Pending Billing'];
    } finally {
      this.loadingQueues = false;
    }
  }

  private loadSavedPreference(): void {
    const savedQueue = AutoModeService.getSavedSpecificQueue();

    if (savedQueue && this.availableQueues.includes(savedQueue)) {
      this.selectedQueue = savedQueue;
    } else {
      // Default to "All Queues" (null)
      this.selectedQueue = null;
    }
  }

  onConfirm(): void {
    AutoModeService.saveSpecificQueue(this.selectedQueue);
    this.$emit('confirmed', this.selectedQueue);
  }

  onCancel(): void {
    this.$emit('cancelled');
  }
}
</script>

<style scoped>
::v-deep .modal-footer .btn-primary {
  background-color: #ff6b35;
  border-color: #ff6b35;
  font-weight: bold;
}

::v-deep .modal-footer .btn-primary:hover,
::v-deep .modal-footer .btn-primary:focus,
::v-deep .modal-footer .btn-primary:active {
  background-color: #e55d2d;
  border-color: #e55d2d;
}
</style>
